import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/Dialog";
import {
  ApiError,
  isOfflineApiError,
  providerApi,
  type CreateFoodItemInput,
  type FoodItemRecord,
} from "../../lib/api";
import { createOfflineRecordId } from "../../lib/offline-store";
import { useAppPreferences } from "../../lib/preferences";
import { useProviderOffline } from "../../lib/provider-offline";
import { getProviderImageErrorMessage, uploadProviderImage } from "../provider-workspace/provider-image-upload";

/** Item photo, or a plain tile when there is none or it fails to load. */
export function MenuThumb({ src }: { src: string | null | undefined }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <span aria-hidden="true" className="menu-stock-thumb" />;
  return <img alt="" className="menu-stock-thumb" onError={() => setFailed(true)} src={src} />;
}

export const FOOD_CATEGORIES = ["drinks", "snacks", "meals", "desserts", "other"];

interface Props {
  item: FoodItemRecord | null;
  venueId: string;
  onClose: () => void;
  onSaved: (item: FoodItemRecord, offline: boolean) => void;
  onDelete?: () => void;
}

/** Add or edit one menu item. Owners only (the API enforces it). */
export function MenuItemDialog({ item, venueId, onClose, onSaved, onDelete }: Props) {
  const { t } = useAppPreferences();
  const { isOnline, queueMutation } = useProviderOffline();
  const [name, setName] = useState(item?.name ?? "");
  const [category, setCategory] = useState(item?.category ?? "drinks");
  const [price, setPrice] = useState(item ? String(item.price) : "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [imageUrl, setImageUrl] = useState(item?.image_url ?? "");
  const [isAvailable, setIsAvailable] = useState(item?.is_available ?? true);
  const [trackStock, setTrackStock] = useState(item?.track_stock ?? false);
  const [openingStock, setOpeningStock] = useState("");
  const [threshold, setThreshold] = useState(item?.low_stock_threshold != null ? String(item.low_stock_threshold) : "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = FOOD_CATEGORIES.includes(category) ? FOOD_CATEGORIES : [...FOOD_CATEGORIES, category];

  const submit = async () => {
    const parsedPrice = Number(price.trim());
    if (!name.trim()) {
      setError(t("Give the item a name."));
      return;
    }
    if (!price.trim() || !Number.isFinite(parsedPrice) || parsedPrice < 0) {
      setError(t("Price must be a valid non-negative number."));
      return;
    }
    const parsedThreshold = threshold.trim() === "" ? null : Number.parseInt(threshold, 10);
    const parsedOpening = openingStock.trim() === "" ? 0 : Number.parseInt(openingStock, 10);
    if ((parsedThreshold !== null && (!Number.isFinite(parsedThreshold) || parsedThreshold < 0))
      || !Number.isFinite(parsedOpening) || parsedOpening < 0) {
      setError(t("Stock numbers must be 0 or more."));
      return;
    }
    const base: CreateFoodItemInput = {
      name: name.trim(),
      category,
      description: description.trim() || null,
      price: parsedPrice,
      image_url: imageUrl.trim() || null,
      is_available: isAvailable,
      track_stock: trackStock,
      low_stock_threshold: parsedThreshold,
    };
    setSaving(true);
    setError(null);
    try {
      if (item) {
        const updated = await providerApi.updateFoodItem(venueId, item.id, {
          ...base,
          ...(parsedThreshold === null ? { low_stock_threshold: undefined, clear_low_stock_threshold: true } : {}),
        });
        onSaved(updated, false);
      } else {
        const created = await providerApi.createFoodItem(venueId, {
          ...base,
          stock_quantity: trackStock ? parsedOpening : 0,
        });
        onSaved(created, false);
      }
    } catch (requestError) {
      if (isOfflineApiError(requestError) && !trackStock) {
        const now = new Date().toISOString();
        if (item) {
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/venues/${venueId}/food/${item.id}`,
            body: base,
            label: `Update food item ${base.name}`,
          });
          onSaved({ ...item, ...base, description: base.description ?? null, image_url: base.image_url ?? null, updated_at: now }, true);
        } else {
          const localId = createOfflineRecordId("food");
          queueMutation({
            method: "POST",
            path: `/api/v1/provider/venues/${venueId}/food`,
            body: base,
            label: `Create food item ${base.name}`,
            local_resource_id: localId,
          });
          onSaved({
            id: localId,
            name: base.name,
            category: base.category,
            description: base.description ?? null,
            price: base.price,
            image_url: base.image_url ?? null,
            is_available: base.is_available ?? true,
            track_stock: false,
            stock_quantity: 0,
            created_at: now,
            updated_at: now,
          }, true);
        }
      } else if (isOfflineApiError(requestError)) {
        setError(t("Stock-tracked items need a connection to save."));
      } else {
        setError(requestError instanceof ApiError ? requestError.detail : t("Could not save food item."));
      }
    } finally {
      setSaving(false);
    }
  };

  const onImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadProviderImage("food", venueId, file);
      setImageUrl(uploaded.signed.public_url);
    } catch (uploadError) {
      setError(getProviderImageErrorMessage(uploadError, t));
    } finally {
      event.target.value = "";
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto">
        <DialogTitle>{item ? t("Edit {name}", { name: item.name }) : t("Add a menu item")}</DialogTitle>
        <DialogDescription>{t("Guests see the name, price, photo and description in the app.")}</DialogDescription>
        <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 sm:col-span-2">
              <span className="ops-label">{t("Name")}</span>
              <input autoFocus className="ops-input" maxLength={255} onChange={(event) => setName(event.target.value)} required value={name} />
            </label>
            <label className="grid gap-1">
              <span className="ops-label">{t("Category")}</span>
              <select className="ops-select" onChange={(event) => setCategory(event.target.value)} value={category}>
                {categories.map((entry) => <option key={entry} value={entry}>{t(entry)}</option>)}
              </select>
            </label>
            <label className="grid gap-1">
              <span className="ops-label">{t("Price (EGP)")}</span>
              <input className="ops-input" inputMode="decimal" min="0" onChange={(event) => setPrice(event.target.value)} required step="0.01" type="number" value={price} />
            </label>
            <label className="grid gap-1 sm:col-span-2">
              <span className="ops-label">{t("Description (optional)")}</span>
              <textarea className="ops-textarea" maxLength={500} onChange={(event) => setDescription(event.target.value)} rows={2} value={description} />
            </label>
            <div className="grid gap-1 sm:col-span-2">
              <span className="ops-label">{t("Photo (optional)")}</span>
              <div className="flex items-center gap-3">
                <MenuThumb src={imageUrl} />
                <input
                  accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                  aria-label={t("Upload photo")}
                  className="ops-input min-w-0 flex-1"
                  disabled={uploading || !isOnline}
                  onChange={(event) => void onImage(event)}
                  type="file"
                />
                {imageUrl ? (
                  <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setImageUrl("")} type="button">{t("Remove")}</button>
                ) : null}
              </div>
              {uploading ? <span className="ops-copy text-xs">{t("Uploading image…")}</span> : null}
              {!isOnline ? <span className="ops-copy text-xs">{t("Image upload is unavailable while offline.")}</span> : null}
            </div>
          </div>

          <label className="ops-checkbox-row">
            <input checked={isAvailable} onChange={(event) => setIsAvailable(event.target.checked)} type="checkbox" />
            <span>
              <span className="ops-label block">{t("On the menu")}</span>
              <span className="ops-copy block text-xs">{t("Turn off to hide it without deleting it.")}</span>
            </span>
          </label>

          <fieldset className="ops-subtle-surface grid gap-3 p-3">
            <label className="ops-checkbox-row">
              <input checked={trackStock} onChange={(event) => setTrackStock(event.target.checked)} type="checkbox" />
              <span>
                <span className="ops-label block">{t("Count stock for this item")}</span>
                <span className="ops-copy block text-xs">{t("Good for cans, bottles and packaged snacks. Orders take from the count and the item hides itself at 0.")}</span>
              </span>
            </label>
            {trackStock ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {!item ? (
                  <label className="grid gap-1">
                    <span className="ops-label">{t("How many do you have now?")}</span>
                    <input className="ops-input" inputMode="numeric" min={0} onChange={(event) => setOpeningStock(event.target.value)} placeholder="0" type="number" value={openingStock} />
                  </label>
                ) : (
                  <p className="ops-copy self-end text-xs">{t("{count} in stock. Use Update stock to change it.", { count: item.stock_quantity ?? 0 })}</p>
                )}
                <label className="grid gap-1">
                  <span className="ops-label">{t("Warn me at (optional)")}</span>
                  <input className="ops-input" inputMode="numeric" min={0} onChange={(event) => setThreshold(event.target.value)} placeholder={t("e.g. 5")} type="number" value={threshold} />
                </label>
              </div>
            ) : null}
          </fieldset>

          {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}

          <div className="flex flex-wrap items-center gap-2">
            {onDelete ? (
              <button className="ops-button" data-variant="danger" onClick={onDelete} type="button">{t("Delete")}</button>
            ) : null}
            <span className="flex-1" />
            <button className="ops-button" data-variant="secondary" onClick={onClose} type="button">{t("Cancel")}</button>
            <button className="ops-button" data-variant="primary" disabled={saving || uploading} type="submit">
              {saving ? t("Saving…") : item ? t("Save changes") : t("Add item")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
